import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { NavLink, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./PanelistDetails.css";

const PanelistDetails = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace("#", ""));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [hash]);

  const panelists = [
    {
      id: "james-borley",
      name: "Mr. James Borley",
      designation: "Director",
      company: "Payment Services at Complyport",
      image: "assets/images/discussion-panalist/MR.-James-Borley.png",
      linkedin: "https://www.linkedin.com/in/james-borley-281669114",
      highlights: [
        "Over 7 years advising clients across a wide range of payment services",
        "Former FCA regulator with 23 years of experience in senior regulatory roles",
        "Held key FCA positions including Head of Passporting, Authorisations Manager, and PSD2 Accountable Executive",
        "Led the implementation of PSD2 into UK regulation during his tenure at the FCA",
        "Member of the European Banking Authority (EBA) working group that developed authorisation guidelines",
        "Contributed to multiple European regulatory working groups shaping payment regulations and guidance",
        "Former Chief Compliance Officer and MLRO at payment firms",
        "Active industry leader: Member of the Payments Association Regulatory Working Group",
        "Director at APCC and former Chair of the FCA Authorisations Liaison Group",
      ],
    },
    {
      id: "teresa-cameron",
      name: "Ms. Teresa Cameron",
      designation: "Group CEO",
      company: "Clear Junction",
      image: "assets/images/discussion-panalist/MS.-Teresa-Cameron.png",
      linkedin: "https://www.linkedin.com/in/teresa-cameron-fct-cgma-1466a916",
      highlights: [
        "Group CEO of Clear Junction, leading a regulated payments and cross-border infrastructure business with strong global growth focus",
        "Finance, treasury, and risk professional with 25+ years of experience across financial services, fintech, media, and charity sectors",
        "Previously served as Group CFO at Clear Junction, playing a key role in scaling the business before becoming CEO",
        "Deep expertise in treasury, FX, risk management, and operational transformation, with a track record of delivering efficiency and strategic change",
        "Fellow of the Association of Corporate Treasurers and a CGMA-qualified finance leader, reflecting strong professional credentials",
        "Active voice in fintech, particularly in payments infrastructure, stablecoins, and B2B settlement innovation",
      ],
    },
    {
      id: "oliver-calma",
      name: "Mr. Oliver Calma",
      designation: "Founder & CEO",
      company: "BC Remit",
      image: "assets/images/discussion-panalist/Mr.-Oliver-Calma.png",
      linkedin: "https://www.linkedin.com/in/oliver-calma-cfa-53976020",
      highlights: [
        "Leads a fintech company transforming cross-border remittances across 24 countries",
        "Experienced international professional with career spanning Southeast Asia, the Middle East, and Europe",
        "Founded BCRemit inspired by the financial challenges faced by migrant workers globally",
        "Driving innovation in remittances through technology, making services more efficient, transparent, and accessible",
        "Chartered Financial Analyst (CFA) charter holder",
        "Holds an MBA and a B.S. in Economics",
      ],
    },
  ];

  return (
    <main className="panelist_details_page page_content">
      <Helmet>
        <title>Discussion Panelists | MSB Association UK</title>
        <meta name="description" content="Meet our expert discussion panelists who are shaping the future of money service businesses in the UK." />
      </Helmet>

      {/* Simplified Page Header */}
      <section className="simple_page_header overflow-hidden">
        <div className="container">
          <motion.h1 
            className="page_title_simple"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Discussion Panelists
          </motion.h1>
          <ul className="breadcrumb_nav unordered_list justify-content-center">
            <li><NavLink to="/">Home</NavLink></li>
            <li className="active"><a href="#!">Panelist Details</a></li>
          </ul>
        </div>
      </section>

      {/* Alternating Panelist Sections */}
      {panelists.map((speaker, index) => (
        <section 
          key={speaker.id} 
          id={speaker.id} 
          className="panelist_section_simple"
        >
          <div className="container">
            <div className={`row align-items-center ${index % 2 !== 0 ? 'flex-row-reverse' : ''}`}>
              <div className="col-lg-5">
                <motion.div 
                  className="simple_img_container text-center"
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="simple_corner dt_top_left"></div>
                  <div className="simple_corner dt_bottom_right"></div>
                  <img src={speaker.image} alt={speaker.name} className="img-fluid simple_portrait" />
                </motion.div>
              </div>
              <div className="col-lg-7">
                <motion.div 
                  className="simple_bio_content"
                  initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <span className="simple_label">Discussion Panelist</span>
                  <h2 className="simple_name">{speaker.name}</h2>
                  <p className="simple_role">{speaker.designation} | {speaker.company}</p>

                  <ul className="simple_highlights_list">
                    {speaker.highlights.map((point, pIdx) => (
                      <li key={pIdx} className="simple_highlight_item">
                        {point}
                      </li>
                    ))}
                  </ul>

                  <a 
                    href={speaker.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="linkedin_link_simple"
                    title={`Connect with ${speaker.name} on LinkedIn`}
                  >
                    <i className="fa-brands fa-linkedin"></i>
                  </a>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      ))}

    </main>
  );
};

export default PanelistDetails;
